import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as Yup from 'yup';
import { Formik } from 'formik';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import IconButton from 'components/@extended/IconButton';
import AnimateButton from 'components/@extended/AnimateButton';
import { resendTwoFactorApi, verifyTwoFactorApi } from 'api/auth';
import { useAuth } from 'contexts/AuthContext';

const validationSchema = Yup.object().shape({
  twoFactorCode: Yup.string()
    .required('Codigo requerido')
    .matches(/^\d{6}$/, 'Introduce un codigo de 6 digitos')
});

const isTerminalSessionError = (err) => {
  const message = String(err?.data?.message || err?.message || '').toLowerCase();
  return (
    err?.data?.message === 'TWO_FACTOR_EXPIRED' ||
    message.includes('token invalido') ||
    message.includes('token inválido') ||
    message.includes('expirado') ||
    message.includes('caducado')
  );
};

export default function SessionReauthDialog() {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { sessionReauthToken, clearSessionReauth, setSession, setSessionReauthToken, logout } = useAuth();
  const [formError, setFormError] = React.useState('');
  const [showCode, setShowCode] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [infoMessage, setInfoMessage] = React.useState('');
  const loginPath = React.useMemo(() => {
    const baseName = (import.meta.env.VITE_APP_BASE_NAME || '').replace(/\/+$/, '');
    return baseName ? `${baseName}/login` : '/login';
  }, []);

  React.useEffect(() => {
    if (!sessionReauthToken) return;
    setFormError('');
    setInfoMessage('');
    setShowCode(false);
    setResendLoading(false);
  }, [sessionReauthToken]);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleBackToLogin = React.useCallback(() => {
    logout();
    window.location.replace(loginPath);
  }, [loginPath, logout]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setFormError('');
    setInfoMessage('');
    try {
      const token = sessionReauthToken;
      if (!token) {
        handleBackToLogin();
        return;
      }

      const { token: authToken, user } = await verifyTwoFactorApi({
        pendingToken: token,
        twoFactorCode: values.twoFactorCode
      });

      setSession(authToken, user);
      clearSessionReauth();
      resetForm();
    } catch (err) {
      if (isTerminalSessionError(err)) {
        handleBackToLogin();
        return;
      } else {
        setFormError(err?.message || 'No se pudo validar el codigo');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setFormError('');
    setInfoMessage('');
    if (!sessionReauthToken) {
      setFormError('No hay una sesion pendiente para reenviar el codigo.');
      return;
    }

    setResendLoading(true);
    try {
      const { pendingToken } = await resendTwoFactorApi({ pendingToken: sessionReauthToken });
      if (pendingToken) {
        setSessionReauthToken(pendingToken);
      }
      setShowCode(false);
      setInfoMessage('Te hemos enviado un nuevo codigo de verificacion.');
    } catch (err) {
      if (isTerminalSessionError(err)) {
        handleBackToLogin();
        return;
      } else {
        setFormError(err?.message || 'No se pudo reenviar el codigo');
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Dialog open={Boolean(sessionReauthToken)} maxWidth="xs" fullWidth disableEscapeKeyDown aria-labelledby="session-reauth-title">
      <DialogTitle id="session-reauth-title">
        <Stack spacing={0.5}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            La sesion ha caducado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Introduce el codigo de verificacion para seguir en la misma pantalla sin perder los cambios.
          </Typography>
        </Stack>
      </DialogTitle>
      <Formik
        initialValues={{
          twoFactorCode: ''
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Stack spacing={2}>
                {formError ? <Alert severity="error">{formError}</Alert> : null}
                {infoMessage ? <Alert severity="success">{infoMessage}</Alert> : null}
                <Alert severity="info">
                  El codigo se usa para restaurar la sesion actual. El presupuesto y el resto de cambios se mantienen en la pagina.
                </Alert>
                <Stack spacing={1}>
                  <InputLabel htmlFor="session-reauth-code">Codigo de doble autenticacion</InputLabel>
                  <OutlinedInput
                    id="session-reauth-code"
                    type={showCode ? 'text' : 'password'}
                    value={values.twoFactorCode}
                    name="twoFactorCode"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="000000"
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    disabled={!sessionReauthToken}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle code visibility"
                          onClick={() => setShowCode((prev) => !prev)}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          color="secondary"
                        >
                          {showCode ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {touched.twoFactorCode && errors.twoFactorCode ? <FormHelperText error>{errors.twoFactorCode}</FormHelperText> : null}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, flexDirection: 'column', gap: 1.25, alignItems: 'stretch' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
                <Button variant="text" onClick={handleResendCode} disabled={!sessionReauthToken || resendLoading || isSubmitting} fullWidth>
                  {resendLoading ? 'Reenviando...' : 'Reenviar codigo'}
                </Button>
                <Button variant="outlined" onClick={handleBackToLogin} fullWidth>
                  Volver al login
                </Button>
              </Stack>
              <AnimateButton>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!sessionReauthToken || isSubmitting}
                  sx={{ minWidth: 160, width: '100%' }}
                >
                  {isSubmitting ? 'Verificando codigo...' : 'Validar codigo'}
                </Button>
              </AnimateButton>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}
